import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';
import './App.css';
import { initGA, trackPageView, trackTechSearch, trackError } from './analytics';

const API_URL = process.env.REACT_APP_API_URL || '';

const DEFAULT_TECHS = ['cursor', 'claude-code', 'github-copilot', 'openai-codex'];

const COLORS = [
  '#22CB5E',
  '#2E5BFF',
  '#FF6B6B',
  '#FFB800',
  '#8B5CF6',
  '#EC4899',
  '#10B981',
  '#F59E0B',
  '#6366F1',
  '#EF4444',
];

function App() {
  const [selectedTechs, setSelectedTechs] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [trendsData, setTrendsData] = useState(null);
  const [firstMentionsData, setFirstMentionsData] = useState(null);
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceRef = useRef(null);

  // Initialize analytics and load data on mount
  useEffect(() => {
    initGA();
    trackPageView(window.location.pathname + window.location.search);

    const params = new URLSearchParams(window.location.search);
    const techsFromUrl = params.get('techs');

    const initialTechs = techsFromUrl
      ? techsFromUrl.split(',').map(t => t.trim()).filter(t => t)
      : DEFAULT_TECHS;

    setSelectedTechs(initialTechs);
    fetchTrends(initialTechs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target) &&
        searchRef.current &&
        !searchRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setSuggestions([]);
      return;
    }
    try {
      const resp = await axios.get(`${API_URL}/api/tech-search`, { params: { q: query } });
      const filtered = resp.data.results.filter(r => !selectedTechs.includes(r.slug));
      setSuggestions(filtered);
      setHighlightedIdx(-1);
    } catch {
      setSuggestions([]);
    }
  }, [selectedTechs]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchText(val);
    setShowSuggestions(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val.trim()), 150);
  };

  const addTech = (slug) => {
    if (!selectedTechs.includes(slug)) {
      setSelectedTechs(prev => [...prev, slug]);
    }
    setSearchText('');
    setSuggestions([]);
    setShowSuggestions(false);
    searchRef.current?.focus();
  };

  const removeTech = (slug) => {
    setSelectedTechs(prev => prev.filter(s => s !== slug));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIdx >= 0 && highlightedIdx < suggestions.length) {
        addTech(suggestions[highlightedIdx].slug);
      } else if (searchText.trim()) {
        // Allow adding raw slug if no suggestion selected
        const slug = searchText.trim().toLowerCase().replace(/\s+/g, '-');
        addTech(slug);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIdx(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIdx(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    } else if (e.key === 'Backspace' && searchText === '' && selectedTechs.length > 0) {
      removeTech(selectedTechs[selectedTechs.length - 1]);
    }
  };

  const fetchTrends = async (techSlugs) => {
    setError(null);
    setLoading(true);

    trackTechSearch(techSlugs);

    try {
      const [trendsResponse, firstMentionsResponse] = await Promise.all([
        axios.post(`${API_URL}/api/trends`, { tech_slugs: techSlugs }),
        axios.post(`${API_URL}/api/first-mentions`, { tech_slugs: techSlugs }).catch(err => {
          console.warn('[WARN] First mentions request failed:', err.message);
          return null;
        }),
      ]);

      setTrendsData(trendsResponse.data.data);

      if (firstMentionsResponse) {
        setFirstMentionsData(firstMentionsResponse.data.data);
      }

      const params = new URLSearchParams();
      params.set('techs', techSlugs.join(','));
      window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
      trackPageView(`${window.location.pathname}?${params}`);
    } catch (err) {
      const errorMessage = err.response?.data?.detail ||
        err.message ||
        'Failed to fetch trend data.';
      setError(`Error: ${errorMessage}`);
      trackError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedTechs.length === 0) {
      setError('Please add at least one technology');
      return;
    }
    setError(null);
    setTrendsData(null);
    setFirstMentionsData(null);
    await fetchTrends(selectedTechs);
  };

  const createPlotlyData = (windowSize) => {
    if (!trendsData) return [];
    return trendsData.map((tech, idx) => {
      const rollingSeries = tech[`rolling_${windowSize}`];
      return {
        x: rollingSeries.dates,
        y: rollingSeries.values,
        type: 'scatter',
        mode: 'lines',
        name: tech.tech_slug,
        line: { color: COLORS[idx % COLORS.length], width: 3 },
      };
    });
  };

  const createFirstMentionsPlotlyData = () => {
    if (!firstMentionsData) return [];
    return firstMentionsData.map((tech, idx) => ({
      x: tech.dates,
      y: tech.values,
      type: 'scatter',
      mode: 'lines',
      name: tech.tech_name || tech.tech_slug,
      line: { color: COLORS[idx % COLORS.length], width: 3 },
    }));
  };

  const plotLayout = (title, windowSize, yAxisTitle) => ({
    title: {
      text: title,
      font: {
        family: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        size: 20,
        color: '#141C51',
      },
    },
    xaxis: {
      title: 'Date',
      showgrid: true,
      gridcolor: 'rgba(0,0,0,0.1)',
      showline: true,
      linecolor: 'lightgray',
      title_font: {
        family: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        size: 14,
      },
    },
    yaxis: {
      title: yAxisTitle || `Job Post Count (${windowSize}-day avg)`,
      showgrid: true,
      gridcolor: 'rgba(0,0,0,0.1)',
      showline: true,
      linecolor: 'lightgray',
      title_font: {
        family: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        size: 14,
      },
    },
    showlegend: true,
    legend: {
      yanchor: 'top',
      y: 0.99,
      xanchor: 'left',
      x: 0.01,
      bgcolor: 'rgba(255, 255, 255, 0.8)',
      bordercolor: '#141C51',
      borderwidth: 1,
    },
    plot_bgcolor: 'white',
    paper_bgcolor: 'white',
    font: {
      family: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      size: 14,
      color: '#141C51',
    },
    autosize: true,
  });

  const formatMentions = (n) => {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return String(n);
  };

  return (
    <div className="App">
      <div className="container">
        <div className="header">
          <a href="https://sumble.com" target="_blank" rel="noopener noreferrer" className="logo-link">
            <img
              src="https://sumble.com/logo512.png"
              alt="Sumble Logo"
              className="logo"
            />
          </a>
          <h1>Technology Trends Analysis</h1>
          <p>Analyze job posting trends for different technologies</p>
        </div>

        <div className="input-section">
          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <label htmlFor="tech-search">
                Technologies
              </label>
              <div className="pill-input-container" onClick={() => searchRef.current?.focus()}>
                {selectedTechs.map(slug => (
                  <span key={slug} className="tech-pill">
                    {slug}
                    <button
                      type="button"
                      className="pill-remove"
                      onClick={(e) => { e.stopPropagation(); removeTech(slug); }}
                      aria-label={`Remove ${slug}`}
                    >
                      &times;
                    </button>
                  </span>
                ))}
                <input
                  ref={searchRef}
                  id="tech-search"
                  type="text"
                  className="pill-search-input"
                  value={searchText}
                  onChange={handleSearchChange}
                  onKeyDown={handleKeyDown}
                  onFocus={() => { if (searchText) setShowSuggestions(true); }}
                  placeholder={selectedTechs.length === 0 ? 'Type to search technologies...' : ''}
                  disabled={loading}
                  autoComplete="off"
                />
              </div>
              {showSuggestions && suggestions.length > 0 && (
                <ul className="suggestions-dropdown" ref={suggestionsRef}>
                  {suggestions.map((s, idx) => (
                    <li
                      key={s.slug}
                      className={`suggestion-item ${idx === highlightedIdx ? 'highlighted' : ''}`}
                      onMouseDown={() => addTech(s.slug)}
                      onMouseEnter={() => setHighlightedIdx(idx)}
                    >
                      <span className="suggestion-name">{s.name}</span>
                      <span className="suggestion-slug">{s.slug}</span>
                      <span className="suggestion-mentions">{formatMentions(s.mentions)} orgs</span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="input-hint">
                Type to search, press Enter or click to add.{' '}
                <a href="https://sumble.com" target="_blank" rel="noopener noreferrer" className="sumble-link">
                  Sign in to Sumble
                </a>
                {' '}to explore the full list.
              </p>
            </div>
            <button type="submit" className="button" disabled={loading || selectedTechs.length === 0}>
              {loading ? 'Loading...' : 'Run Analysis'}
            </button>
          </form>
        </div>

        {error && (
          <div className="error">
            <p>{error}</p>
          </div>
        )}

        {loading && (
          <div className="loading">
            <div className="loading-spinner"></div>
            <p>Loading trend data...</p>
          </div>
        )}

        {(trendsData || firstMentionsData) && !loading && (
          <div className="charts-section">
            {firstMentionsData && (
              <div className="chart-container">
                <h2 className="chart-title">First Mentions (28-Day Rolling Window)</h2>
                <p className="chart-description">
                  This chart shows the number of companies mentioning a technology <strong>for the first time</strong> in a job posting.
                  Recruiters typically repeat the same job posting over and over, so raw job post counts are a lagging indicator of technology adoption.
                  A first mention, however, represents an active decision by a company to include a technology in their hiring requirements.
                  This signals that the technology is being adopted or seriously considered.
                  The number of companies with a first mention is a contemporaneous — or possibly even a leading — indicator of adoption.
                </p>
                <Plot
                  data={createFirstMentionsPlotlyData()}
                  layout={plotLayout('First Mentions (28-Day Rolling)', 28, 'Companies with First Mention')}
                  config={{ responsive: true }}
                  style={{ width: '100%', height: '600px' }}
                  useResizeHandler={true}
                />
              </div>
            )}

            {trendsData && (
              <div className="chart-container">
                <h2 className="chart-title">Active Job Posts (28-Day Rolling Average)</h2>
                <p className="chart-description">
                  This chart shows the rolling average of daily active job posts mentioning each technology.
                  This is likely a <strong>lagging indicator</strong> of adoption: there is momentum in what appears on job posts,
                  and leaving a technology on a job posting is often a passive decision.
                  A technology can continue to appear in active postings long after a company has stopped actively investing in it.
                </p>
                <Plot
                  data={createPlotlyData(28)}
                  layout={plotLayout('Active Job Posts (28-Day Avg)', 28)}
                  config={{ responsive: true }}
                  style={{ width: '100%', height: '600px' }}
                  useResizeHandler={true}
                />
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

export default App;
